import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "testify-inline-flex testify-items-center testify-justify-center testify-whitespace-nowrap testify-rounded-md testify-text-sm testify-font-medium testify-transition-colors testify-focus-visible:outline-none testify-focus-visible:ring-1 testify-focus-visible:ring-ring testify-disabled:pointer-events-none testify-disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "testify-bg-primary testify-text-primary-foreground testify-shadow hover:testify-bg-primary/90",
        destructive:
          "testify-bg-destructive testify-text-destructive-foreground testify-shadow-sm hover:testify-bg-destructive/90",
        outline:
          "testify-border testify-border-input testify-bg-background testify-shadow-sm hover:testify-bg-accent hover:testify-text-accent-foreground",
        secondary:
          "testify-bg-secondary testify-text-secondary-foreground testify-shadow-sm hover:testify-bg-secondary/80",
        ghost: "hover:testify-bg-accent hover:testify-text-accent-foreground",
        link: "testify-text-primary testify-underline-offset-4 hover:testify-underline",
      },
      size: {
        default: "testify-h-9 testify-px-4 testify-py-2",
        sm: "testify-h-8 testify-rounded-md testify-px-3 testify-text-xs",
        lg: "testify-h-10 testify-rounded-md testify-px-8",
        icon: "testify-h-9 testify-w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
